export const meta = {
  id: '02-basics-1',
  order: 2,
  checkFocus: { numbers: ['pl'] },   // what the end-of-lesson check tests first
  titleFi: 'Perusteet 1',
  titleEn: 'Basics 1',
  duolingoUnit: 'duo-01-basics-1',
  lemmaIds: ['jiōrna', 'sȳz', 'vala', 'sagon'],
  exampleIds: [
    'ex-2eeafab69c26',
    'ex-539dd2489f31',
    'hotd-s1e05-004',
    'ex-b95f4e0bde15',
    'got-s7e02-003',
    'got-s5e09-003',
    'ex-15de9915a9b0',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Basics_1',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Stress',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Number',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Case',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Noun_Phrases',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Subject_Pronouns',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Coordination',
    'https://wiki.languageinvention.com/index.php?title=Vala',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['adj-position', 'coordination'],
  grammarAlso: ['letters', 'word-order'],
  drills: { numbers: ['pl'] },
  showLines: ['hotd-s1e05-004', 'got-s7e02-003', 'got-s5e09-003'],
  pronunciation: { focus: 'lengthening', lines: ['got-s7e02-003', 'got-s5e09-003'] },
  phrases: ['jiōrna'],
  letter: {
    fi: 'Kerro kolmella lauseella, millainen kumppanisi on ja millaisia asiat ympärilläsi ovat: sinä olet…, se on… ja ne ovat…',
    en: 'In three sentences, say what your partner and the things around you are like: you are…, it is… and they are…',
  },
  scene: 'got-s5e09-1',
  // Omat virheet (docs/COURSE.md): fill and case answers from the first lesson check with exercises
  ownErrors: ['exercise'],
}

# Perusteet 1

<Pronounce l="jiōrna" word="jiōrna" />

<Hv glossed>Jiōrna!</Hv> – tervetuloa. Tällä sanalla Duolingo aloittaa korkeavalyrian kurssinsa, ja sillä alkaa tämäkin oppitunti.

Korkeavalyria on muinaisen Valyrian kieli. Valyria tuhoutui arvoituksellisessa katastrofissa vuosisatoja ennen *Game of Thronesin* tapahtumia. Se oli lohikäärmeiden kesyttäjien ja sotureiden kieli, mutta tarinan aikaan se on hienostuneisuuden ja sivistyksen kieli, muisto menneestä ajasta. Sitä puhuvat Hullu kuningas Aerys, Aegon Valloittaja ja Daenerys Targaryen.

<Example id="ex-2eeafab69c26" />

## Kieli, joka taipuu

Korkeavalyria on taivuttava kieli. Substantiivin muoto kertoo, mikä sen tehtävä lauseessa on, ja verbi taipuu <Term id="aikamuoto">aikamuodon</Term>, <Term id="aspekti">aspektin</Term> ja <Term id="paaluokka">pääluokan</Term> (aktiivi tai passiivi) mukaan. Tämä on teille tuttua: suomessakin *talo*, *talon* ja *talossa* ovat saman sanan muotoja eri tehtävissä.

Kaksi järjestystä kannattaa painaa mieleen heti:

- **Adjektiivi tulee yleensä ennen substantiivia**, kuten suomessa: <Hv glossed>sȳz vala</Hv> ’hyvä mies’.
- **Verbi tulee lauseen loppuun.** Perusjärjestys on subjekti–objekti–verbi. Suomen tavallinen järjestys on toinen (*mies näkee naisen*), joten tässä suomesta ei ole apua.

Korkeavalyriassa ei myöskään ole artikkeleita. Sana <Hv glossed>vala</Hv> voi tarkoittaa ’mies’, ’eräs mies’ tai ’se mies’ – aivan kuten suomen *mies*. Englanninpuhujan on opeteltava tämä erikseen, teille se on itsestään selvää.

## Äänteet suomalaisen korvin

Äänteet käytiin perusteellisesti läpi ensimmäisellä oppitunnilla. Tässä kertaus Duolingon Tips & Notes -ohjeista suomalaisen näkökulmasta: mikä sujuu itsestään ja missä pitää olla tarkkana.

**Tutut konsonantit.** *b, d, h, l, m, n* äännetään suunnilleen niin kuin luulisi. *g* on aina kova [g]. *k, p, t* ovat aspiroimattomia eli niissä ei ole englannin henkäystä – tämän suomalainen tekee valmiiksi oikein. *s* on aina soinniton, kuten suomessa. *r* on täryäänne kuten suomen *r*. *v* äännetään nykyään [v], vanhemmassa kielessä [w].

**Näissä tarkkana:**

- **z** on soinnillinen [z]. Suomessa sitä ei juuri ole, joten varo, ettei se muutu huomaamatta s:ksi.
- **j** äännetään nykyään kuten englannin sanassa *jam*, [dʒ], eikä suomen j:n tapaan. Vanhempi, klassinen ääntämys oli [ɟ], suunnilleen kuin unkarin *gy*, ja sen näet sanakirjan IPA-merkinnöissä.
- **q** on k, joka äännetään paljon taempana: kielen selkä koskettaa kitakielekettä, [q]. Vastinetta ei ole suomessa eikä englannissa.
- **gh** on soinnillinen kurkkuäänne, [ɣ] tai [ʁ].
- **lj** on liudentunut l, [ʎ], kuten *lli* englannin sanassa *million*.
- **ñ** on [ɲ], kuten espanjan *ñ*.
- **rh** on soinniton r.

**Vokaalit** *a, e, i, o, u* ovat puhtaita ja selviä. *e* ei ole koskaan äänetön, eikä e:n ja o:n avoimuudella ole väliä. Ja sitten se, mikä tekee korkeavalyriasta suomalaiselle helpon: **y on suomen y**. Englanninpuhujille sitä joudutaan selittämään ”i:nä pyöristetyin huulin”, teille se on vain *y*.

**Pitkät vokaalit** merkitään viivalla: *ā, ē, ī, ō, ū, ȳ*. Ne ovat samat vokaalit pitkinä – täsmälleen kuin suomen *aa* tai *yy*. Duolingossa pitkän vokaalin voi kirjoittaa kahdella kirjaimella: *yy* tarkoittaa ȳ:tä. Suomalaisen sormille se on luontevaa. Puhelimen valyrian näppäimistössä pitkät vokaalit ovat ylärivillä (<LessonLink n={1}>oppitunti 1</LessonLink>).

<Pronounce word="sȳz" />

Pituus erottaa sanoja samaan tapaan kuin suomessa *tuli* ja *tuuli*. Tästä on teille paljon hyötyä: korkeavalyriassa pelkkä vokaalin pituus voi kantaa kieliopillista tietoa, ja sen te kuulette ilman harjoittelua.

## Yksikkö ja monikko

Duolingon ensimmäisessä osiossa opetellaan sanoja yksikössä ja monikossa. Monikon voi muodostaa monella tavalla, joten katso tarkkaan, mihin sana päättyy.

<Paradigm lemma="vala" cases="nom" numbers="sg,pl" />

<Hv glossed>Vala</Hv> ’mies’ on monikossa <Seam glossed s="val" e="i" /> ’miehet’. Myöhemmin tulee vastaan sanoja, joiden monikko näyttää aivan toiselta.

Korkeavalyrian substantiivit taipuvat suvun, luvun ja sijan mukaan, ja adjektiivi mukautuu kaikkiin kolmeen. Tässä vaiheessa riittää luku: kun substantiivi on monikossa, sen edellä oleva adjektiivikin on monikossa.

<Compare glossed fi="hyvä|t miehe|t" hv="sȳ|ri val|i" label="monikko: adjektiivi mukautuu lukuun" />

Tässä suomi auttaa: suomessakin adjektiivi mukautuu lukuun ja sijaan (*hyvä mies*, *hyvät miehet*, *hyvässä talossa*). Suvun mukaan suomen adjektiivi ei taivu koskaan, eikä korkeavalyrian suvuille ole suomessa mitään vastinetta. Niihin palataan omalla oppitunnillaan.

<Note>
Lukuja on korkeavalyriassa itse asiassa neljä: yksikkö, monikko, <Term id="kollektiivi">kollektiivi</Term> (koko joukko, kaikki) ja <Term id="muutama">muutama</Term> (pieni joukko). Kahta viimeistä suomessa ei ole kieliopillisena lukuna, joten ne opetellaan erikseen <LessonLink n={28}>oppitunnilla 28</LessonLink>. Toistaiseksi riittävät yksikkö ja monikko. Kaikki neljä ovat Kieliopissa (<TopicLink id="numbers">Luvut</TopicLink>).
</Note>

## Adjektiivin paikka

Adjektiivi on tavallisesti substantiivin edellä, mutta se voi tulla myös sen jälkeen – tyylisyistä tai jotta lause ei kävisi liian raskaaksi. ’Hyvä mies’ on siis joko <Hv glossed>sȳz vala</Hv> tai <Hv glossed>vala sȳz</Hv>. Petersonin mukaan merkitys ei muutu: kumpikin järjestys on oikein.

Suomessa *mies hyvä* kuulostaa runolliselta, mutta valyriassa jälkiasema on aivan tavallinen. Vain <Term id="maarite">määräävät sanat</Term>, kuten demonstratiivit, ovat yleensä edellä.

<Example id="ex-539dd2489f31" />

Tässä <Hv glossed>sȳz</Hv> ei ole substantiivin määrite, vaan se kertoo, millainen miekka on: ’pitkämiekka on hyvä’.

Yksi asia kannattaa tietää jo nyt, koska suomi ei valmista siihen: osa adjektiivien päätteistä riippuu siitä, onko adjektiivi substantiivin edellä vai jäljessä. Duolingon ohjeiden esimerkissä ’johtajani’ (monikossa) on <Hv glossed>ñuhyz jentyssy</Hv>, mutta käänteisessä järjestyksessä <Hv glossed>jentyssy ñuhyzy</Hv>. Tähän palataan aurinkosuvun oppitunnilla.

<Line id="hotd-s1e05-004" />

Daemonin repliikissä näkyvät molemmat asiat: adjektiivi on edellä (<Hv glossed>sȳz vala</Hv> ’hyvä mies’), ja pronomini jää pois, mihin palataan kohta.

## ”Ja” ilman ja-sanaa

Tämä on korkeavalyrian kauneimpia piirteitä. Kun kaksi tai kolme samanarvoista sanaa liitetään yhteen, ja-sanaa ei tavallisesti käytetä. Sen sijaan **luettelon viimeisen sanan loppuvokaali pidennetään ja paino siirtyy sanan viimeiselle tavulle**.

<Compare glossed fi="mies ja nainen" hv="vala ābrā" label="ja-sana ~ loppuvokaalin pidennys" />

Duolingon ohjeiden mukaan <Hv fi="mies ja nainen">vala ābrā</Hv> äännetään *VA-la ā-BRĀ*: paino on viimeisellä tavulla. Luettelossa muuttuu vain viimeinen sana, muut pysyvät ennallaan. Sama keino toimii substantiiveilla, adjektiiveilla ja jopa verbeillä.

Pituuden kuulette helposti, koska suomessa pituus on arkipäivää. Painon siirtyminen on vaikeampaa: suomalainen korva odottaa painoa ensimmäiselle tavulle, ja *ā-BRĀ* tuntuu aluksi väärältä. Se ei ole väärin. Pane siis merkille pitkät vokaalit lauseen lopussa – ne voivat tarkoittaa ”ja”.

<Example id="ex-b95f4e0bde15" />

’Tuli ja veri’: <Hv glossed>ānogar</Hv> ’veri’ saa pitkän vokaalin, eikä ja-sanaa tarvita.

<Line id="got-s7e02-003" />

Melisandre luettelee: <Hv glossed>sindita liortā</Hv> ’ostettu ja myyty’, <Hv glossed>qilōnta ozbartā</Hv> ’ruoskittu ja poltinmerkitty’. Kummassakin parissa jälkimmäisen sanan loppu on pidentynyt.

<Line id="got-s5e09-003" />

Tässä yhdistetään kaksi verbiä: ’taistelen ja kuolen’. Jälkimmäisen verbin loppu on pidentynyt.

Ja-sana <Hv l="se">se</Hv> on kyllä olemassa. Wikin kieliopin mukaan sitä käytetään esimerkiksi peräkkäisten adjektiivien ja lauseiden välissä sekä silloin, kun ”ja” halutaan korostaa. Daemonin repliikissä se yhdistää kaksi pidempää ilmausta: <Hv glossed>sȳz vala se gīmije azantys</Hv> ’hyvä mies ja hieno ritari’.

## Pronomini jää pois

Persoonapronominit opetellaan <LessonLink n={18}>oppitunnilla 18</LessonLink>, ja ne ovat myös <TopicLink id="personal">Kieliopissa</TopicLink>. Siihen asti riittää tämä: kun lauseessa on verbi, subjekti on joko lauseen alussa tai se on pronomini, jota ei sanota ääneen. Duolingon ohjeiden esimerkki on <Hv glossed>Vala issa</Hv>. Sanatarkasti se olisi ’mies on’, mutta luonteva käännös on *Hän on mies*. ’Hän’ puuttuu lauseesta, koska verbin muoto riittää kertomaan, kenestä puhutaan.

Suomi auttaa tässä puoliksi. Sanomme *olen mies* ja *olet mies* ilman pronominia, koska verbin pääte kertoo persoonan. Yleiskielessä kolmas persoona tarvitsee kuitenkin pronominin: *hän on mies*, ei pelkkä *on mies*. Korkeavalyria jättää pronominin pois myös kolmannessa persoonassa. Pronomini sanotaan ääneen lähinnä silloin, kun sitä halutaan korostaa.

<Example id="ex-15de9915a9b0" />

<Interlinear hv="Sylv|ie val|a iksā." gloss="viisas-NOM mies-NOM olla-PRS.2SG" fi="Olet viisas mies." />

<Interlinear hv="Korz|e sȳ|z issa." gloss="pitkämiekka-NOM hyvä-NOM olla-PRS.3SG" fi="Pitkämiekka on hyvä." />

<Hv glossed>Iksā</Hv> on ’olet’ ja <Hv glossed>issa</Hv> ’on’. Ne ovat saman verbin muotoja, eikä kumpikaan tarvitse pronominia rinnalleen. Kaikki kuusi persoonaa opit <LessonLink n={3}>seuraavalla oppitunnilla</LessonLink>.

## Harjoitellaan

<Practice hv="Sȳz vala issa." fi="Hän on hyvä mies." />

<Practice hv="Sȳri vali issi." fi="He ovat hyviä miehiä." />

<Practice hv="Sȳz ābra issa." fi="Hän on hyvä nainen." />

Huomaa toisessa lauseessa, että kaikki kolme sanaa ovat monikossa: <Hv fi="hyvät">sȳri</Hv>, <Hv>vali</Hv> ja verbi <Hv glossed>issi</Hv> ’ovat’.

<Vocab />

<Check />
